<template>
  <main-template>
    <v-container fluid class="pa-0">
      <!-- Title & Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div>
              <h1 class="mb-1 text-h4 font-weight-bold d-flex align-center">
                <v-btn color="primary" icon class="mr-2" @click="goBack">
                  <v-icon left> fa fa-angle-left </v-icon>
                </v-btn>
                Alunos sem Entrada (3 Dias+)
              </h1>
              <div class="text-subtitle-1 grey--text text--darken-1">
                Alunos com ausência consecutiva de registro no controle facial Notifiq
              </div>
            </div>
            <div class="d-flex align-center flex-wrap">
              <v-btn
                v-if="alunosList.length > 0"
                color="primary"
                outlined
                class="mr-2 font-weight-bold"
                @click="marcarTodasComoLidas"
              >
                <v-icon left>mdi-check-all</v-icon>
                Marcar todas como lidas
              </v-btn>
              <v-chip
                v-if="paginate.total"
                color="error lighten-5"
                text-color="error"
                class="font-weight-medium text-subtitle-2 px-4 py-2"
                label
              >
                {{ paginate.total }} alunos identificados
              </v-chip>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Filters Card -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card outlined class="filters-card elevation-1">
            <v-card-title class="pb-1 text-subtitle-1 font-weight-medium">
              <v-icon left size="20" color="primary">mdi-filter-variant</v-icon>
              Filtros de Pesquisa
            </v-card-title>
            <v-card-text>
              <v-row>
                <!-- Escola Filter -->
                <v-col cols="12" sm="6" md="3">
                  <e-autocomplete
                    :items="escolas"
                    item-text="descricao"
                    item-value="id"
                    v-model="filters.escola_id"
                    label="Escola"
                    placeholder="Selecione a Escola"
                    dense
                    solo
                    clearable
                    hide-details
                  />
                </v-col>

                <!-- Turma Filter -->
                <v-col cols="12" sm="6" md="3">
                  <e-autocomplete
                    :items="filteredTurmas"
                    item-text="descricao"
                    item-value="id"
                    v-model="filters.turma_id"
                    label="Turma"
                    placeholder="Selecione a Turma"
                    dense
                    solo
                    clearable
                    hide-details
                  />
                </v-col>

                <!-- Serie Filter -->
                <v-col cols="12" sm="6" md="2">
                  <e-autocomplete
                    :items="series"
                    item-text="descricao"
                    item-value="id"
                    v-model="filters.serie_id"
                    label="Série"
                    placeholder="Selecione a Série"
                    dense
                    solo
                    clearable
                    hide-details
                  />
                </v-col>

                <!-- Nome Filter -->
                <v-col cols="12" sm="6" md="2">
                  <v-text-field
                    v-model="filters.nome"
                    label="Nome do Aluno"
                    placeholder="Pesquisar por nome..."
                    dense
                    solo
                    hide-details
                    clearable
                  />
                </v-col>

                <!-- Matricula Filter -->
                <v-col cols="12" sm="6" md="2">
                  <v-text-field
                    v-model="filters.matricula"
                    label="Matrícula"
                    placeholder="ID ou Código..."
                    dense
                    solo
                    hide-details
                    clearable
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card outlined class="elevation-1">
            <v-data-table
              :headers="headers"
              :items="alunosList"
              :loading="loading"
              :hide-default-footer="true"
              class="alunos-ausentes-table"
              no-data-text="Nenhum aluno ausente com os filtros informados."
              loading-text="Carregando lista de alunos ausentes..."
            >
              <!-- Slot for Aluno Name -->
              <template v-slot:item.nome="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="32" color="error lighten-5" class="mr-3">
                    <span class="error--text font-weight-bold subtitle-2">{{
                      item.aluno?.nomecompleto ? item.aluno.nomecompleto.charAt(0) : "A"
                    }}</span>
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold text-subtitle-2">
                      {{ item.aluno?.nomecompleto }}
                    </div>
                    <div class="caption text--secondary">
                      Escola: {{ item.turma?.franquia?.descricao || "N/A" }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Slot for Matricula -->
              <template v-slot:item.matricula="{ item }">
                <span class="font-weight-medium grey--text text--darken-3">
                  {{ item.codigo || item.id }}
                </span>
              </template>

              <!-- Slot for Turma -->
              <template v-slot:item.turma="{ item }">
                <span>{{ item.turma?.descricao || "N/A" }}</span>
              </template>

              <!-- Slot for Serie -->
              <template v-slot:item.serie="{ item }">
                <span>{{ item.serie?.descricao || "N/A" }}</span>
              </template>

              <!-- Slot for Ultima Entrada -->
              <template v-slot:item.ultima_entrada="{ item }">
                <span v-if="item.data_ultima_entrada" class="text-subtitle-2 font-weight-medium">
                  {{ formatDateTime(item.data_ultima_entrada) }}
                </span>
                <v-chip v-else small color="amber lighten-5" text-color="amber darken-4" label>
                  Sem registro de entrada
                </v-chip>
              </template>

              <!-- Slot for Dias Ausentes -->
              <template v-slot:item.dias_ausentes="{ item }">
                <v-chip
                  small
                  class="font-weight-bold"
                  :color="getDiasAusentesColor(item.dias_sem_entrada)"
                  dark
                  label
                >
                  {{ item.dias_sem_entrada }} dias úteis
                </v-chip>
              </template>

              <!-- Slot for Status -->
              <template v-slot:item.status>
                <v-chip
                  small
                  color="red lighten-5"
                  text-color="red darken-2"
                  class="font-weight-bold"
                  label
                >
                  Ausente
                </v-chip>
              </template>

              <!-- Slot for Action (Mark as Read) -->
              <template v-slot:item.action="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                      @click="marcarComoLida(item)"
                    >
                      <v-icon>mdi-check</v-icon>
                    </v-btn>
                  </template>
                  <span>Marcar como lida</span>
                </v-tooltip>
              </template>
            </v-data-table>

            <!-- Custom Pagination -->
            <div class="py-4 border-top">
              <e-paginate
                :current_page="paginate.current_page"
                :last_page="paginate.last_page"
                :pageNumber="pageNumber"
                @changeActionResponse="onPageChange"
              />
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </main-template>
</template>

<script>
export default {
  name: "AlunosAusentesPage",
  data() {
    return {
      loading: false,
      escolas: [],
      turmas: [],
      series: [],
      alunosList: [],
      pageNumber: 0,
      paginate: {
        current_page: 0,
        last_page: 0,
        total: 0,
      },
      filters: {
        escola_id: null,
        turma_id: null,
        serie_id: null,
        nome: "",
        matricula: "",
      },
      headers: [
        { text: "Aluno / Escola", value: "nome", sortable: false },
        { text: "Matrícula", value: "matricula", sortable: false },
        { text: "Turma", value: "turma", sortable: false },
        { text: "Série", value: "serie", sortable: false },
        { text: "Última Entrada Registrada", value: "ultima_entrada", sortable: false },
        {
          text: "Dias Consecutivos Sem Entrada",
          value: "dias_ausentes",
          align: "center",
          sortable: false,
        },
        { text: "Status", value: "status", align: "center", sortable: false },
        { text: "Ação", value: "action", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    filteredTurmas() {
      if (!this.filters.escola_id) return this.turmas;
      return this.turmas.filter(
        (t) => parseInt(t.configuracao_id, 10) === parseInt(this.filters.escola_id, 10)
      );
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pageNumber = 0;
        this.fetchData();
      },
    },
  },
  async mounted() {
    this.loading = true;
    this.$loaderService.open("Carregando alunos ausentes...");
    try {
      await Promise.all([
        this.fetchEscolas(),
        this.fetchTurmas(),
        this.fetchSeries(),
        this.fetchData(),
      ]);
    } catch (e) {
      this.$handleError(e);
    } finally {
      this.loading = false;
      this.$loaderService.close();
    }
  },
  methods: {
    async fetchEscolas() {
      try {
        this.escolas = await this.$services.franquiasService.syncAll();
      } catch (e) {
        console.error("Erro ao carregar escolas:", e);
      }
    },
    async fetchTurmas() {
      try {
        this.turmas = await this.$services.turmasService.syncAllGeral();
      } catch (e) {
        console.error("Erro ao carregar turmas:", e);
      }
    },
    async fetchSeries() {
      try {
        this.series = await this.$services.seriesService.syncAll();
      } catch (e) {
        console.error("Erro ao carregar séries:", e);
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        const queryParams = {
          page: this.pageNumber + 1,
          escola_id: this.filters.escola_id,
          turma_id: this.filters.turma_id,
          serie_id: this.filters.serie_id,
          nome: this.filters.nome,
          matricula: this.filters.matricula,
        };
        const response = await this.$services.presencasService.getAlunosAusentes(queryParams);
        this.alunosList = response.data || [];
        this.paginate = {
          current_page: (response.current_page || 1) - 1,
          last_page: response.last_page || 1,
          total: response.total || 0,
        };
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.loading = false;
      }
    },
    async marcarComoLida(item) {
      try {
        this.$loaderService.open("Processando...");
        await this.$services.presencasService.marcarComoLido(item.id);
        this.$toast.success(`Notificação do aluno ${item.aluno?.nomecompleto} marcada como lida.`);
        this.fetchData();
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.$loaderService.close();
      }
    },
    async marcarTodasComoLidas() {
      try {
        this.$loaderService.open("Processando...");
        const queryParams = {
          escola_id: this.filters.escola_id,
          turma_id: this.filters.turma_id,
          serie_id: this.filters.serie_id,
          nome: this.filters.nome,
          matricula: this.filters.matricula,
        };
        await this.$services.presencasService.marcarTodasComoLidas(queryParams);
        this.$toast.success(
          "Todas as notificações correspondentes aos filtros foram marcadas como lidas."
        );
        this.fetchData();
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.$loaderService.close();
      }
    },
    onPageChange(pageNumber) {
      this.pageNumber = pageNumber;
      this.fetchData();
    },
    goBack() {
      this.$router.push({ name: "home" });
    },
    getDiasAusentesColor(dias) {
      if (dias >= 5) return "red darken-3";
      if (dias >= 4) return "red darken-1";
      return "orange darken-2";
    },
    formatDateTime(value) {
      if (!value) return "";
      try {
        const date = new Date(value);
        return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      } catch (e) {
        return value;
      }
    },
  },
};
</script>

<style scoped>
.filters-card {
  background-color: #fafafa;
}
.alunos-ausentes-table >>> .v-data-table__wrapper {
  border-radius: 4px;
}
.border-top {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
